import { useCallback, useEffect, useSyncExternalStore } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { chatAPI, eventsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Logger } from '../utils/logger';
import { eventIdsFrom, seenEventsKey, unseenEvents } from '../utils/inboxUnread';

export interface InboxUnread {
  chat: number;
  events: number;
}

// One shared copy, so the Inbox tabs, the bottom Inbox icon and the web sidebar agree.
let state: InboxUnread = { chat: 0, events: 0 };
let currentEventIds: string[] = [];
const listeners = new Set<() => void>();
const emit = (next: InboxUnread) => {
  state = next;
  listeners.forEach(l => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => state;

// The tabs, the bottom icon and the sidebar may all ask at once - one request serves them.
let inFlight: Promise<void> | null = null;
let lastOwner = '';
function loadUnread(ownerId: string): Promise<void> {
  lastOwner = ownerId;
  if (!inFlight) inFlight = fetchUnread(ownerId).finally(() => { inFlight = null; });
  return inFlight;
}

async function fetchUnread(ownerId: string): Promise<void> {
  const [chatRes, eventsRes] = await Promise.allSettled([chatAPI.getUnreadCount(), eventsAPI.getEvents()]);

  let chat = state.chat;
  if (chatRes.status === 'fulfilled') {
    chat = Number(chatRes.value.data?.unreadCount ?? chatRes.value.data?.UnreadCount ?? 0) || 0;
  } else {
    Logger.warn('InboxUnread', 'Chat unread count failed', chatRes.reason);
  }

  let events = state.events;
  if (eventsRes.status === 'fulfilled') {
    currentEventIds = eventIdsFrom(eventsRes.value.data);
    try {
      const stored = await AsyncStorage.getItem(seenEventsKey(ownerId));
      const result = unseenEvents(currentEventIds, stored ? JSON.parse(stored) : null);
      if (!stored) await AsyncStorage.setItem(seenEventsKey(ownerId), JSON.stringify(result.seen));
      events = result.count;
    } catch (e) {
      Logger.warn('InboxUnread', 'Seen events could not be read', e);
    }
  }

  emit({ chat, events });
}

/** Reload the counts from outside a component - e.g. once a chat has been marked read. */
export function refreshInboxUnread(): void {
  if (lastOwner) loadUnread(lastOwner).catch(() => {});
}

/**
 * The Inbox unread counts (utils/inboxUnread): Chat from the server, Events by what this
 * parent has been shown. Reloads when a chat message arrives over the live connection.
 */
export function useInboxUnread() {
  const { user } = useAuth();
  const ownerId = user?.userId || user?.id || '';
  const { notifications } = useNotifications();
  const unread = useSyncExternalStore(subscribe, snapshot, snapshot);

  const refresh = useCallback(() => {
    if (ownerId) loadUnread(ownerId).catch(e => Logger.warn('InboxUnread', 'Refresh failed', e));
  }, [ownerId]);

  // A live chat message means the chat count has moved.
  const chatMessages = notifications.filter(n => n.eventType === 'Chat').length;
  useEffect(() => {
    refresh();
  }, [refresh, chatMessages]);

  /** The Events tab was opened: every event listed now counts as seen. */
  const markEventsSeen = useCallback(async () => {
    if (!ownerId || state.events === 0) return;
    try {
      const stored = await AsyncStorage.getItem(seenEventsKey(ownerId));
      const seen = new Set<string>(stored ? JSON.parse(stored) : []);
      currentEventIds.forEach(id => seen.add(id));
      await AsyncStorage.setItem(seenEventsKey(ownerId), JSON.stringify([...seen]));
    } catch (e) {
      Logger.warn('InboxUnread', 'Seen events could not be saved', e);
    }
    emit({ ...state, events: 0 });
  }, [ownerId]);

  return { ...unread, total: unread.chat + unread.events, refresh, markEventsSeen };
}
